import React, {useState} from 'react';
import {LoginPayloadRequest} from '../../Context/AuthProvider/AuthProviderActions';
import {useAuthState} from '../../Context/AuthProvider/AuthProvider';
import {Form, Button, Row} from 'antd';
import {ExclamationCircleOutlined, UserOutlined, LockOutlined} from '@ant-design/icons';

import {useLocation, useNavigate} from 'react-router-dom';
import {addAlert} from '../../Context/AlertProvider/AlertProviderActions';
import {useAlertDispatcherState} from '../../Context/AlertProvider/AlertProvider';
import {DiazarFormInput} from './DiazarFormInput';
import {CompanyInputForm} from './CompanyInputForm';


interface ViewProps {
    validCompany: string
    setValidCompany: React.Dispatch<React.SetStateAction<string>>

    handleLogin?: (payload: LoginPayloadRequest) => Promise<void>
    setUseAut0?: React.Dispatch<React.SetStateAction<boolean>>
    loginOnlyDiazar?: boolean

    hasError?: string
}

export const LoginForm: React.FunctionComponent<ViewProps> = (props) => {
    const {validCompany, setValidCompany, hasError} = props;
    const  loginOnlyDiazar = props.loginOnlyDiazar !== undefined ? props.loginOnlyDiazar : false;

    const [username, setUserName] = useState<string>('');
    const [isValidUsername, setIsValidUserName] = useState<boolean>(false);

    const [password, setPassword] = useState<string>('');
    const [isValidPassword, setIsValidPassword] = useState<boolean>(false);

    const [company, setCompany] = useState<string>('');
    const [isValidCompany, setIsValidCompany] = useState<boolean>(false);
    const [hasValidCompany, setHasValidCompany] = useState<boolean>(false);

    const [validCompanyErrorMessage, setValidCompanyErrorMessage] = useState<string>();

    const navigate = useNavigate();
    const location = useLocation();

    const {loading, errorMessage} = useAuthState();

    const alertDispatch = useAlertDispatcherState();

    const handleLogin = React.useCallback(async () => {
        const payload: LoginPayloadRequest = {company: company, username: username, password: password};
        if (props.handleLogin !== undefined) {
            await props.handleLogin(payload);
        }
    }, [company, username, password, props]);


    const isValidForm = React.useMemo<boolean>(() => {
        const isLoading = (loading !== undefined) ? loading : false;
        return !isLoading && hasValidCompany && isValidCompany && isValidUsername && isValidPassword;

    }, [loading, hasValidCompany, isValidCompany, isValidUsername, isValidPassword]);

    const helperText = React.useMemo(() => {
        let msg : string | undefined = undefined;

        if(errorMessage !== undefined){
            msg = errorMessage;
        }else if( hasError !== undefined ){
            msg = hasError;
        }else{
            const msgSessionStorage = sessionStorage.getItem('error-message');
            if (msgSessionStorage !== null) {
                msg = msgSessionStorage;

                setTimeout(()=>{
                    sessionStorage.removeItem('error-message');
                }, 5000);
            }
        }

        if (msg === undefined) {
            return null;
        }

        return (
            <Form.Item help={
                <span style={{color: 'red'}}>
                    <ExclamationCircleOutlined/> &nbsp;{msg}
                </span>
            }/>
        );

    }, [errorMessage, hasError]);

    const handleIsValidCompanyAction = React.useCallback((response: { status: number; type: number }) => {
        if (props.setUseAut0) {
            props.setUseAut0(response.type !== 0);
        }

        if (loginOnlyDiazar){
            const isValid = response.status === 1 && response.type === 0;
            setHasValidCompany(isValid);

            if (isValid) {
                setValidCompany(company);
            } else {
                setValidCompany('');
                const message = 'La empresa ya ha sido migrada para usar auth0, intenta con otra.';
                setValidCompanyErrorMessage(message);
                addAlert('Error', message, 'error', alertDispatch);
            }
        }

    }, [alertDispatch, company, loginOnlyDiazar, props, setValidCompany]);

    const loadingForm = React.useMemo(() => {
        return (loading !== undefined) ? loading : false;
    }, [loading]);

    return (
        <React.Fragment>
            <div className="auth-wrapper">
                <div className="auth-inner">
                    <Form
                        labelCol={{span: 24}}
                        wrapperCol={{span: 24}}
                        autoComplete="off"
                        layout="vertical"
                        style={{textAlign: 'justify'}}
                    >
                        {helperText}

                        <CompanyInputForm
                            setIsValidCompany={ setIsValidCompany }
                            handleIsValidCompanyAction={handleIsValidCompanyAction}
                            setCompany={setCompany}
                            company={company}
                            isLoadingForm={loadingForm}
                            hasValidCompany={hasValidCompany}
                            isValidCompany={isValidCompany}
                            setValidCompanyErrorMessage={setValidCompanyErrorMessage}
                            setHasValidCompany={setHasValidCompany}
                            validCompanyErrorMessage={validCompanyErrorMessage}
                            validCompany={validCompany}
                            setValidCompany={setValidCompany}
                            refSelector="login-clave"
                        />

                        <DiazarFormInput id="login-clave" label="Clave" isRequired
                                         type="text"
                                         placeholder="Clave de Usuario"
                                         getState={(isValid, value) => {
                                             setIsValidUserName(isValid);
                                             setUserName(value);
                                         }}
                                         initialValue={username}
                                         isReadOnly={loadingForm || !isValidCompany || !hasValidCompany}
                                         prefix={<UserOutlined/>}
                                         onPressEnter={ (isValidForm) ? handleLogin : undefined }
                        />

                        <DiazarFormInput id="login-password" label="Contraseña" isRequired
                                         type="password"
                                         placeholder="Contraseña de Usuario"
                                         getState={(isValid, value) => {
                                             setIsValidPassword(isValid);
                                             setPassword(value);
                                         }}
                                         initialValue={password}
                                         isReadOnly={loadingForm || !isValidCompany || !hasValidCompany}
                                         prefix={<LockOutlined/>}
                                         onPressEnter={ (isValidForm) ? handleLogin : undefined }
                        />

                        <Row/>
                        <Form.Item className="btn-form-actions">
                            <Button
                                type="primary"
                                onClick={e => {
                                    e.preventDefault();
                                    handleLogin();
                                }}
                                block
                                disabled={!isValidForm}
                                loading={loadingForm}
                            >
                                Inicia sesión
                            </Button>
                            <Button
                                type="link"
                                onClick={e => {
                                    e.preventDefault();
                                    sessionStorage.setItem('login-redirect', location.pathname);
                                    navigate('/password-reset-request');
                                }}
                                block
                                >
                                ¿Olvidaste tu contraseña?
                            </Button>
                        </Form.Item>
                    </Form>
                </div>
            </div>
        </React.Fragment>
    );
};
